/* Where a tooltip goes: above what it names, centred on it, inside the window.
   Pure and here rather than beside the component, because the edges and the
   corners are the part worth testing without a browser. */
export interface TipAnchor {
  top: number;
  bottom: number;
  /** Where the bubble would like to be centred: the cursor, or the middle. */
  cx: number;
}

export interface TipPlacement {
  top: number;
  left: number;
  /** Where the arrow sits along the bubble's own width. */
  arrow: number;
  below: boolean;
}

/** Between the bubble and what it names. */
const GAP = 10;
/** And between the bubble and the window. */
const EDGE = 8;
/** Never nearer a corner than this, so the arrow keeps the card above it. */
const ARROW_INSET = 14;

export function placeTip(
  anchor: TipAnchor,
  box: { width: number; height: number },
  view: { width: number; height: number },
): TipPlacement {
  const above = anchor.top - GAP - box.height;
  /* Above by default, below when there is no room above and there is below.
     When neither side can hold it — a tall bubble in a short window — it goes
     to whichever side has more room and is clamped there: pinned to the top
     it covered the very thing it was naming. */
  const roomAbove = anchor.top - GAP - EDGE;
  const roomBelow = view.height - anchor.bottom - GAP - EDGE;
  const fitsAbove = box.height <= roomAbove;
  const fitsBelow = box.height <= roomBelow;
  /* And below for anything in the top third of the window even when it would
     fit above: those rows sit under a sticky bar, and a bubble opened over
     one covers the name of the very thing the reader is looking at. */
  const highUp = anchor.top < view.height / 3;
  const below = fitsAbove && !highUp ? false : fitsBelow ? true : roomBelow > roomAbove;
  const room = Math.max(EDGE, view.width - box.width - EDGE);
  const left = Math.min(Math.max(anchor.cx - box.width / 2, EDGE), room);
  return {
    top: below
      ? Math.min(anchor.bottom + GAP, Math.max(EDGE, view.height - EDGE - box.height))
      : Math.max(EDGE, above),
    left,
    // The arrow stays on the thing being named while the window's edge moves
    // the bubble, and stops short of the bubble's own corners.
    arrow: Math.min(
      Math.max(anchor.cx - left, ARROW_INSET),
      Math.max(ARROW_INSET, box.width - ARROW_INSET),
    ),
    below,
  };
}
